import { useMemo, useRef, useState } from 'react';
import type { LayoutTemplateSlot } from '@/lib/api';
import { scalePath } from '@/lib/scalePath';

export interface PhotoFitInSlot {
  /** 1 = object-fit:cover at slot bounds; >1 zooms in. */
  scale: number;
  /** % of bbox width — positive shifts the photo right inside the clip. */
  offsetX: number;
  /** % of bbox height — positive shifts down. */
  offsetY: number;
}

interface TemplatedPhotoSlotProps {
  slot: LayoutTemplateSlot;
  /** Resolved photo URL, or null for empty drop target. */
  photoUrl: string | null;
  photoFit?: PhotoFitInSlot;
  /** Editor only — pass true to render selection affordances. */
  interactive?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  /** When provided + isSelected, customer can pan the photo inside the clip. */
  onFitChange?: (fit: PhotoFitInSlot) => void;
  /** Container dims used to scale clip-path from unit to pixel space. */
  pageWidthPx: number;
  pageHeightPx: number;
  /**
   * Drag-and-drop wiring. When `interactive`, the editor passes the photo id
   * currently filling this slot (used as the HTML5 drag payload for slot↔slot
   * swap) and the drop handler that should fire when something is dropped on
   * this slot. Matches the existing PhotoSlot convention: payload carries
   * `photoId` + `photoSource` ('slot' | 'sidebar') via `dataTransfer`.
   */
  photoIdForDrag?: string | null;
  onDrop?: (photoId: string, source: 'slot' | 'sidebar') => void;
  /**
   * dnd-kit-style id, e.g. `slot:${pageNumber}:${slotIdx}`. Required by the
   * Task 23 contract so editor wiring can identify slots. Currently used only
   * as an opaque key for diagnostics — the actual drag/drop pipeline uses
   * HTML5 dataTransfer to interop with the existing photo-strip/PhotoSlot
   * drag system. Read-only renders (preview/PDF) can pass any unique string.
   */
  droppableId: string;
}

export function TemplatedPhotoSlot({
  slot,
  photoUrl,
  photoFit,
  interactive = false,
  isSelected = false,
  onSelect,
  onFitChange,
  pageWidthPx,
  pageHeightPx,
  photoIdForDrag = null,
  onDrop,
  // droppableId is intentionally unused at runtime — it's part of the
  // public contract for Task 23 so callers can identify slots, and so that
  // future migration to @dnd-kit/core can swap implementations without
  // touching call sites.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  droppableId: _droppableId,
}: TemplatedPhotoSlotProps) {
  const slotW = slot.width * pageWidthPx;
  const slotH = slot.height * pageHeightPx;
  const [isOver, setIsOver] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const dropEnabled = interactive && !!onDrop;
  const dragEnabled = interactive && !!photoUrl && !!photoIdForDrag;

  const scaledClipPath = useMemo(() => {
    if (!slot.clip_path) return null;
    return scalePath(slot.clip_path, slotW, slotH);
  }, [slot.clip_path, slotW, slotH]);

  const fit = photoFit ?? { scale: 1, offsetX: 0, offsetY: 0 };

  // Drag-to-pan inside the clip — only when selected, has photo, and the
  // parent registered an onFitChange. Read-only renders (preview/PDF) skip.
  const dragRef = useRef<{ startX: number; startY: number; origOffsetX: number; origOffsetY: number } | null>(null);
  const onPointerDown = (e: React.MouseEvent) => {
    if (!interactive || !isSelected || !photoUrl || !onFitChange) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origOffsetX: fit.offsetX,
      origOffsetY: fit.offsetY,
    };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ((ev.clientX - dragRef.current.startX) / slotW) * 100;
      const dy = ((ev.clientY - dragRef.current.startY) / slotH) * 100;
      onFitChange({
        scale: fit.scale,
        offsetX: dragRef.current.origOffsetX + dx,
        offsetY: dragRef.current.origOffsetY + dy,
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── HTML5 drag-and-drop handlers ────────────────────────────────────────
  // Mirrors the existing PhotoSlot convention so the photo strip and the
  // grid-based PhotoSlot can drop onto / drag from templated slots without
  // a parallel dnd system. dataTransfer carries `photoId` + `photoSource`.
  const dragHandlers = dropEnabled
    ? {
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          if (!isOver) setIsOver(true);
        },
        onDragEnter: (e: React.DragEvent) => {
          e.preventDefault();
          setIsOver(true);
        },
        onDragLeave: () => setIsOver(false),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          setIsOver(false);
          const pid = e.dataTransfer.getData('photoId');
          const src =
            (e.dataTransfer.getData('photoSource') as 'slot' | 'sidebar') ||
            'sidebar';
          if (pid && onDrop) onDrop(pid, src);
        },
      }
    : {};

  const onDragStart = dragEnabled
    ? (e: React.DragEvent) => {
        if (!photoIdForDrag) return;
        e.dataTransfer.setData('photoId', photoIdForDrag);
        e.dataTransfer.setData('photoSource', 'slot');
        e.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
      }
    : undefined;

  // Visual feedback while a draggable is hovering this slot.
  const overEmptyBg = isOver ? 'rgba(201, 162, 74, 0.28)' : 'rgba(201, 162, 74, 0.10)';
  const overFilledBoxShadow = isOver ? 'inset 0 0 0 3px #C9A24A' : undefined;

  return (
    <div
      onClick={interactive ? onSelect : undefined}
      onMouseDown={onPointerDown}
      draggable={dragEnabled}
      onDragStart={onDragStart}
      onDragEnd={() => setIsDragging(false)}
      // Fix #7 (2026-05-28): expose the slot identifier to the touch-drag
      // pipeline. useTouchDragPhoto uses elementFromPoint + this attribute
      // to locate the slot under the finger on touchend.
      data-slot-drop-target={dropEnabled ? _droppableId : undefined}
      {...dragHandlers}
      onContextMenu={interactive ? (e) => e.preventDefault() : undefined}
      style={{
        position: 'absolute',
        left: `${slot.x * 100}%`,
        top: `${slot.y * 100}%`,
        // E4 — same as PhotoSlot: no iOS long-press Save/Copy callout on the
        // editing canvas (inherits to the <img> children).
        ...(interactive ? { WebkitTouchCallout: 'none' as const, WebkitUserSelect: 'none' as const, userSelect: 'none' as const } : {}),
        width: `${slot.width * 100}%`,
        height: `${slot.height * 100}%`,
        transform: slot.rotation ? `rotate(${slot.rotation}deg)` : undefined,
        transformOrigin: 'center',
        zIndex: slot.z ?? 0,
        clipPath: scaledClipPath ? `path('${scaledClipPath}')` : undefined,
        outline: interactive && isSelected ? '2px solid #C9A24A' : undefined,
        outlineOffset: '-2px',
        // Inset highlight when a draggable is hovering a FILLED slot — the
        // outline above is reserved for selection state.
        boxShadow: photoUrl ? overFilledBoxShadow : undefined,
        cursor: !interactive
          ? undefined
          : isSelected && photoUrl
            ? 'move'
            : 'pointer',
      }}
    >
      {photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: `${50 + fit.offsetX}% ${50 + fit.offsetY}%`,
            transform: fit.scale !== 1 ? `scale(${fit.scale})` : undefined,
            transformOrigin: 'center',
            maxWidth: 'none',
            maxHeight: 'none',
            // Slot-to-slot drag fades the source while it's being dragged
            // — same affordance the photo strip uses.
            opacity: isDragging ? 0.3 : 1,
          }}
        />
      ) : (
        <DropTarget hasClipPath={!!scaledClipPath} isOver={isOver} overBg={overEmptyBg} />
      )}
    </div>
  );
}

function DropTarget({
  hasClipPath,
  isOver = false,
  overBg = 'rgba(201, 162, 74, 0.10)',
}: {
  hasClipPath: boolean;
  isOver?: boolean;
  overBg?: string;
}) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: overBg,
        // Dashed border outside the clip would get chopped; for clipped
        // slots the colored fill alone communicates the shape. Solid when
        // a draggable is hovering for a stronger affordance.
        border: hasClipPath
          ? undefined
          : `2px ${isOver ? 'solid' : 'dashed'} #C9A24A`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#946815',
        fontSize: 12,
        fontWeight: 500,
        textAlign: 'center',
        padding: 8,
        boxSizing: 'border-box',
        transition: 'background 120ms ease, border-color 120ms ease',
      }}
    >
      {isOver ? 'Déposer' : 'Glissez une photo ici'}
    </div>
  );
}
